import { TEST_IDS, Z_INDEX } from '@va/constants';
import { isTabletDevice } from '@va/dashboard/selectors/ui';
import { ChatBotIcon } from '@va/icons';
import { useTranslate } from '@va/localization';
import { Button, ButtonProps } from '@va/ui/design-system';
import { useVisualViewportSize } from '@va/util/hooks';
import { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { AiChatbot } from './AiChatbot';

type AiChatbotButtonProps = {
  questionsCount?: number;
} & Partial<ButtonProps>;

export const AiChatbotButton = ({ questionsCount, ...props }: AiChatbotButtonProps) => {
  const translate = useTranslate();
  const isTablet = useSelector(isTabletDevice);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  const { height: viewportHeight } = useVisualViewportSize(200);

  return (
    <Button
      ref={buttonRef}
      icon={() => <ChatBotIcon className='sm:w-18px sm:h-18px' />}
      tooltip={<AiChatbot questionsCount={questionsCount} />}
      tooltipProps={{
        trigger: 'click',
        outsidePress: () => false,
        useDefaultStyle: false,
        tooltipClassNames: 'h-[45rem]',
        zIndex: Z_INDEX.chat,
        ancestorScroll: false,
        placement: 'bottom',
        open: isTooltipOpen,
        onOpenChange: (open) => {
          if (!open) {
            buttonRef.current?.focus();
          }
          setIsTooltipOpen(open);
        },
        offset: ({ y, rects: { floating } }) => {
          const centeredTopY = (viewportHeight - floating.height) / 2;
          const offset = -y + centeredTopY;

          if (isTablet) return { mainAxis: offset };
          return {};
        },
        offsetDependencies: [viewportHeight, isTablet],
      }}
      text={translate('aiAssistant.chatbotButton.label')}
      data-testid={TEST_IDS.specific.aiAssistant}
      {...props}
    />
  );
};
